import { useState, useEffect, useRef, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import {
  collection, doc, onSnapshot, addDoc, updateDoc, deleteDoc,
  query, orderBy, serverTimestamp, getDoc, setDoc, limit, getDocs, writeBatch,
} from 'firebase/firestore';
import { db } from '../services/firebase-init';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../contexts/AuthContext';
import { useConfirm } from '../components/ConfirmDialog';
import { useNotificationCounts } from '../contexts/NotificationCountsContext';
import {
  MessageSquare, Mail, DollarSign, History, Users, CalendarDays, Paperclip,
  Trash2, X, Archive, ArchiveRestore, Image, Check, Pencil, CheckCircle2,
  Circle, Zap, Smartphone, Copy, User, Home, Loader2, FileText, ArrowLeft,
} from 'lucide-react';
import { noAutofill } from '../utils/noAutofill';

const INK    = '#2B2620';
const BROWN  = '#8B4513'; // WALNUT
const MOSS   = '#3B6B44';
const OCHRE  = '#A6752A';
const RUST   = '#A13D2D';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';

const FONT_DISPLAY = "'Fraunces', Georgia, serif";
const FONT_SANS    = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

// ── Pay date logic ────────────────────────────────────────────────────────────
const PAY_ANCHOR = new Date('2026-03-27T00:00:00');
function startOfDay(d) { const x = new Date(d); x.setHours(0,0,0,0); return x; }
function calcNextPayDate(from = new Date()) {
  const anchor = startOfDay(PAY_ANCHOR), today = startOfDay(from);
  const diffDays = Math.round((today - anchor) / 86400000);
  let daysUntil;
  if (diffDays <= 0) { daysUntil = Math.abs(diffDays); }
  else { const rem = diffDays % 14; daysUntil = rem === 0 ? 0 : 14 - rem; }
  const next = new Date(today); next.setDate(next.getDate() + daysUntil); return next;
}
function calcWeekEnding(payDate) { const d = new Date(payDate); d.setDate(d.getDate() - 7); return d; }
function fmtDateShort(d) { return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }); }
function fmtDateLong(d)  { return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }); }
function payDateLabel(payDate) {
  const today = startOfDay(new Date()), pay = startOfDay(new Date(payDate));
  const diff = Math.round((pay - today) / 86400000), long = fmtDateLong(pay);
  if (diff === 0) return `Today — ${long}`;
  if (diff === 1) return `Tomorrow — ${long}`;
  if (diff <= 6)  return `This ${long}`;
  return long;
}

// ── Relative time (matches iOS relativeTime helper) ───────────────────────────
function relativeTime(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1)  return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const card = { background: PAPER, borderRadius: '16px', border: '1px solid rgba(139,69,19,0.1)', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', overflow: 'hidden', marginBottom: '28px' };
const cardHeader = (accent = BROWN) => ({ padding: '18px 24px 16px', borderBottom: '1px solid rgba(139,69,19,0.08)', background: `${accent}0d`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' });
const bdg = (bg, color) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '2px 9px', borderRadius: '999px', background: bg, color, fontSize: '11px', fontWeight: '700' });
const mkBtn = (variant = 'primary', disabled = false) => {
  const base = { padding: '8px 18px', borderRadius: '8px', border: 'none', fontSize: '12px', fontWeight: '700', cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS, transition: 'all 0.15s', opacity: disabled ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', gap: '6px' };
  if (variant === 'primary') return { ...base, background: BROWN, color: 'white', boxShadow: disabled ? 'none' : '0 2px 8px rgba(139,69,19,0.25)' };
  if (variant === 'ghost')   return { ...base, background: 'rgba(139,69,19,0.07)', color: BROWN };
  if (variant === 'danger')  return { ...base, background: 'rgba(161,61,45,0.08)', color: RUST };
  if (variant === 'green')   return { ...base, background: 'rgba(59,107,68,0.1)', color: MOSS };
  if (variant === 'blue')    return { ...base, background: OCHRE, color: 'white', boxShadow: disabled ? 'none' : '0 2px 8px rgba(166,117,42,0.25)' };
  if (variant === 'teal')    return { ...base, background: MOSS, color: 'white', boxShadow: disabled ? 'none' : '0 2px 8px rgba(59,107,68,0.25)' };
  return base;
};
const inp = { padding: '8px 12px', borderRadius: '8px', border: '1px solid rgba(139,69,19,0.2)', fontSize: '13px', fontFamily: FONT_SANS, outline: 'none', background: '#faf8f5', color: INK, width: '100%', boxSizing: 'border-box' };

function FL({ label, children, style }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', ...style }}>
      <div style={{ fontSize: '10px', fontWeight: '700', color: '#8C8275', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
      {children}
    </div>
  );
}
function formatTs(ts) {
  if (!ts) return '—';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}
function proxyUrl(url) {
  if (!url) return url;
  if (!url.includes('api.twilio.com') && !url.includes('media.twiliocdn.com')) return url;
  return `https://getmediaproxy-42vla7okqq-uc.a.run.app?url=${encodeURIComponent(url)}`;
}
function normalizePhone(raw) {
  if (!raw) return '';
  return String(raw).replace(/\D/g, '').slice(-10);
}
function isUnresolved(name) {
  if (!name || !name.trim()) return true;
  const n = name.trim().toLowerCase();
  if (n === 'new contact' || n === 'unknown') return true;
  if (/^\+?[\d\s\-().]+$/.test(name.trim())) return true;
  return false;
}

// ── Date separator label ──────────────────────────────────────────────────────
function dateSeparatorLabel(date) {
  const d = date.toDate ? date.toDate() : new Date(date);
  const today = startOfDay(new Date());
  const msgDay = startOfDay(d);
  const diff = Math.round((today - msgDay) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7) return d.toLocaleDateString('en-US', { weekday: 'long' });
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Assigned-to chip (matches iOS green/orange pill) ─────────────────────────
function AssignedChip({ name, small = false }) {
  const assigned = name && name.trim();
  const bg    = assigned ? 'rgba(59,107,68,0.12)'  : 'rgba(166,117,42,0.12)';
  const color = assigned ? '#3B6B44'               : '#A6752A';
  const label = assigned ? name                    : 'Unassigned';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: small ? '2px 7px' : '3px 9px',
      borderRadius: '999px', background: bg, color,
      fontSize: small ? '9px' : '10px', fontWeight: '700',
      whiteSpace: 'nowrap', flexShrink: 0,
    }}>{label}</span>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// LANDING
// ═══════════════════════════════════════════════════════════════════════════════
function Landing({ onSelect, unreadSms, pendingPayroll, sentEmailCount, isOfficeManager }) {
  const allSections = [
    { id: 'sms',     Icon: MessageSquare, label: 'SMS Inbox',          desc: 'Send and receive text messages with customers',   color: OCHRE, badge: unreadSms > 0 ? unreadSms : null,        badgeBg: RUST,  badgeLabel: `${unreadSms} unread`,       everyone: true  },
    { id: 'email',   Icon: Mail,          label: 'Direct Email',        desc: 'Send branded emails to customers or employees',   color: MOSS,  badge: sentEmailCount > 0 ? sentEmailCount : null, badgeBg: MOSS,  badgeLabel: `${sentEmailCount} sent`,  everyone: false },
    { id: 'payroll', Icon: DollarSign,    label: 'Payroll & Pay Stubs', desc: 'Upload and send biweekly pay stubs to employees', color: BROWN, badge: pendingPayroll > 0 ? pendingPayroll : null, badgeBg: OCHRE, badgeLabel: `${pendingPayroll} pending`, everyone: false },
  ];
  const sections = allSections.filter(s => s.everyone || isOfficeManager);
  return (
    <div style={{ fontFamily: FONT_SANS }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ fontFamily: FONT_DISPLAY, fontSize: '24px', fontWeight: 600, color: INK, marginBottom: '4px' }}>Communications</div>
        <div style={{ fontSize: '13px', color: STONE }}>Select where you want to go</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: '20px' }}>
        {sections.map(s => (
          <button key={s.id} onClick={() => onSelect(s.id)}
            style={{ background: PAPER, border: '2px solid rgba(139,69,19,0.1)', borderRadius: '20px', padding: '28px 24px', cursor: 'pointer', fontFamily: FONT_SANS, textAlign: 'left', transition: 'all 0.2s', boxShadow: '0 2px 12px rgba(139,69,19,0.06)', position: 'relative', overflow: 'hidden' }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 28px rgba(139,69,19,0.14)'; e.currentTarget.style.borderColor = s.color + '44'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(139,69,19,0.06)'; e.currentTarget.style.borderColor = 'rgba(139,69,19,0.1)'; }}>
            {s.badge && <div style={{ position: 'absolute', top: '18px', right: '18px', background: s.badgeBg, color: 'white', borderRadius: '999px', fontSize: '12px', fontWeight: '800', padding: '2px 10px' }}>{s.badge > 99 ? '99+' : s.badge}</div>}
            <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', boxShadow: `0 4px 14px ${s.color}33` }}><s.Icon size={24} strokeWidth={2} color="white" /></div>
            <div style={{ fontSize: '17px', fontWeight: '800', color: INK, marginBottom: '6px' }}>{s.label}</div>
            <div style={{ fontSize: '12px', color: STONE, lineHeight: '1.5' }}>{s.desc}</div>
            {s.badge && <div style={{ marginTop: '14px', display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '999px', background: s.badgeBg + '18', color: s.badgeBg, fontSize: '11px', fontWeight: '700' }}>{s.badgeLabel}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PAYROLL SECTION
// ═══════════════════════════════════════════════════════════════════════════════
function PayrollSection({ onBack }) {
  const functions = getFunctions();
  const uploadFn = httpsCallable(functions, 'uploadPayStub');
  const sendNowFn = httpsCallable(functions, 'sendPayStubNow');
  const deleteEmpFn = httpsCallable(functions, 'deleteEmployee');
  const confirm = useConfirm();
  const [employees, setEmployees] = useState([]);
  const [stubs, setStubs] = useState([]);
  const [showAddEmp, setShowAddEmp] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [savingEmp, setSavingEmp] = useState(false);
  const [uploadingFor, setUploadingFor] = useState(null);
  const [sendingNow, setSendingNow] = useState(null);
  const [deletingEmp, setDeletingEmp] = useState(null);
  const [scheduleMode, setScheduleMode] = useState({});
  const [customDate, setCustomDate] = useState({});
  const [tab, setTab] = useState('roster');
  const fileRef = useRef({});
  const nextPay = calcNextPayDate(), weekEndDay = calcWeekEnding(nextPay);
  const sendLabel = payDateLabel(nextPay), weekEnd = fmtDateShort(weekEndDay);

  useEffect(() => { const u = onSnapshot(query(collection(db, 'payrollEmployees'), orderBy('name')), s => setEmployees(s.docs.map(d => ({ id: d.id, ...d.data() })))); return u; }, []);
  useEffect(() => { const u = onSnapshot(query(collection(db, 'payStubs'), orderBy('uploadedAt', 'desc'), limit(100)), s => setStubs(s.docs.map(d => ({ id: d.id, ...d.data() })))); return u; }, []);

  async function addEmployee() {
    if (!newName.trim() || !newEmail.trim()) return; setSavingEmp(true);
    try { await addDoc(collection(db, 'payrollEmployees'), { name: newName.trim(), email: newEmail.trim().toLowerCase(), active: true, createdAt: serverTimestamp() }); setNewName(''); setNewEmail(''); setShowAddEmp(false); }
    catch(e) { alert('Failed: ' + e.message); } finally { setSavingEmp(false); }
  }
  async function toggleActive(emp) { await updateDoc(doc(db, 'payrollEmployees', emp.id), { active: !emp.active }); }
  async function deleteEmployee(emp) {
    if (!(await confirm({ title: `Permanently delete ${emp.name}?`, message: 'This cannot be undone.', tone: 'danger', confirmText: 'Delete' }))) return;
    setDeletingEmp(emp.id);
    try { await deleteEmpFn({ employeeId: emp.id }); } catch(e) { alert('Failed: ' + e.message); } finally { setDeletingEmp(null); }
  }
  async function handleFile(empId, file) {
    if (!file || file.type !== 'application/pdf') { alert('Please select a PDF.'); return; } setUploadingFor(empId);
    try {
      const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(file); });
      const scheduledFor = scheduleMode[empId] && customDate[empId] ? new Date(customDate[empId]).toISOString() : null;
      const result = await uploadFn({ employeeId: empId, pdfBase64: base64, fileName: file.name, scheduledFor });
      const d = result.data; alert(`Pay stub queued!\n\nPay period ending: ${d.weekEnding}\nWill send: ${d.sendLabel} at 12:00 AM CT`);
      setScheduleMode(p => ({ ...p, [empId]: false })); setCustomDate(p => ({ ...p, [empId]: '' }));
    } catch(e) { alert('Upload failed: ' + (e.message || String(e))); } finally { setUploadingFor(null); }
  }
  async function sendNow(stubId, name) {
    if (!(await confirm({ title: `Send pay stub to ${name} right now?`, confirmText: 'Send' }))) return; setSendingNow(stubId);
    try { await sendNowFn({ stubId }); alert(`Sent to ${name}.`); } catch(e) { alert('Failed: ' + e.message); } finally { setSendingNow(null); }
  }

  const activeEmps = employees.filter(e => e.active), inactiveEmps = employees.filter(e => !e.active);
  const pending = stubs.filter(s => s.status === 'pending'), sent = stubs.filter(s => s.status === 'sent'), failed = stubs.filter(s => s.status === 'failed');
  const stubsFor = id => stubs.filter(s => s.employeeId === id);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
        <button style={mkBtn('ghost')} onClick={onBack}><ArrowLeft size={13} strokeWidth={2} /> Back</button>
        <div><div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 600, color: '#2B2620' }}>Payroll & Pay Stubs</div><div style={{ fontSize: '12px', color: '#8C8275', marginTop: '2px' }}>Next pay date: <strong style={{ color: BROWN }}>{sendLabel}</strong> · Pay period ending: <strong>{weekEnd}</strong></div></div>
      </div>
      <div style={card}>
        <div style={cardHeader(BROWN)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: BROWN, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><DollarSign size={18} strokeWidth={2} color="white" /></div>
            <div><div style={{ fontSize: '15px', fontWeight: '800', color: '#2B2620' }}>{activeEmps.length} active employee{activeEmps.length !== 1 ? 's' : ''}</div><div style={{ fontSize: '11px', color: '#8C8275' }}>PDFs deleted immediately after sending · biweekly schedule</div></div>
          </div>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            {pending.length > 0 && <span style={bdg('rgba(166,117,42,0.15)', '#A6752A')}>{pending.length} pending</span>}
            <button style={mkBtn('ghost')} onClick={() => setTab(t => t === 'roster' ? 'history' : 'roster')}>{tab === 'roster' ? <><History size={13} strokeWidth={2} /> History</> : <><Users size={13} strokeWidth={2} /> Roster</>}</button>
            <button style={mkBtn('primary')} onClick={() => setShowAddEmp(v => !v)}>{showAddEmp ? <><X size={13} strokeWidth={2} /> Cancel</> : '+ Add Employee'}</button>
          </div>
        </div>
        {showAddEmp && (
          <div style={{ padding: '14px 24px', background: 'rgba(139,69,19,0.03)', borderBottom: '1px solid rgba(139,69,19,0.08)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
              <FL label="Full Name"><input value={newName} onChange={e => setNewName(e.target.value)} name="twp-comm-emp-name" {...noAutofill} placeholder="e.g. Noah Kwizera" style={inp} /></FL>
              <FL label="Email"><input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="noah@thewindowplacellc.com" type="email" name="twp-comm-emp-email" {...noAutofill} style={inp} /></FL>
              <button style={mkBtn('primary', savingEmp || !newName.trim() || !newEmail.trim())} disabled={savingEmp || !newName.trim() || !newEmail.trim()} onClick={addEmployee}>{savingEmp ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}
        <div style={{ padding: '20px 24px' }}>
          {tab === 'roster' && (
            <div>
              <div style={{ padding: '14px 18px', background: 'rgba(139,69,19,0.05)', border: '1px solid rgba(139,69,19,0.15)', borderRadius: '12px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                <CalendarDays size={22} strokeWidth={2} color={BROWN} />
                <div><div style={{ fontSize: '13px', fontWeight: '700', color: BROWN }}>Next Automatic Send</div><div style={{ fontSize: '14px', fontWeight: '800', color: '#2B2620', marginTop: '2px' }}>{sendLabel} at 12:00 AM CT</div><div style={{ fontSize: '11px', color: '#8C8275', marginTop: '2px' }}>For pay period ending {weekEnd} · Biweekly every 14 days</div></div>
              </div>
              {employees.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#8C8275', fontSize: '13px' }}>No employees yet.</div>}
              {activeEmps.map(emp => {
                const empPending = stubsFor(emp.id).find(s => s.status === 'pending'), empLastSent = stubsFor(emp.id).find(s => s.status === 'sent');
                const uploading = uploadingFor === emp.id, deleting = deletingEmp === emp.id, useCustom = scheduleMode[emp.id] || false;
                const pendingPayDate = empPending?.scheduledFor?.toDate ? empPending.scheduledFor.toDate() : null;
                const pendingSendLabel = pendingPayDate ? payDateLabel(pendingPayDate) : null, pendingWeekEnd = pendingPayDate ? fmtDateShort(calcWeekEnding(pendingPayDate)) : null;
                return (
                  <div key={emp.id} style={{ background: '#faf8f5', border: `1px solid ${empPending ? 'rgba(166,117,42,0.4)' : 'rgba(139,69,19,0.1)'}`, borderRadius: '12px', padding: '14px 16px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: BROWN, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '800', fontSize: '13px', flexShrink: 0 }}>{emp.name.split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase()}</div>
                        <div><div style={{ fontSize: '14px', fontWeight: '700', color: '#2B2620' }}>{emp.name}</div><div style={{ fontSize: '11px', color: '#8C8275' }}>{emp.email}</div></div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {empPending && <div style={{ textAlign: 'right' }}><span style={bdg('rgba(166,117,42,0.15)', '#A6752A')}>Pending</span>{pendingSendLabel && <div style={{ fontSize: '10px', color: '#A6752A', marginTop: '3px', fontWeight: '600' }}>Sends: {pendingSendLabel} at 12:00 AM CT</div>}{pendingWeekEnd && <div style={{ fontSize: '10px', color: '#8C8275', marginTop: '1px' }}>Pay period ending: {pendingWeekEnd}</div>}</div>}
                        {!empPending && empLastSent && <span style={bdg('rgba(59,107,68,0.1)', '#3B6B44')}>Last sent {formatTs(empLastSent.sentAt)}</span>}
                        <input type="file" accept="application/pdf" style={{ display: 'none' }} ref={el => { fileRef.current[emp.id] = el; }} onChange={e => { if (e.target.files[0]) handleFile(emp.id, e.target.files[0]); e.target.value = ''; }} />
                        <button style={mkBtn('primary', uploading || !!empPending)} disabled={uploading || !!empPending} onClick={() => fileRef.current[emp.id]?.click()}>{uploading ? <><Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> Uploading…</> : <><Paperclip size={13} strokeWidth={2} /> Upload Pay Stub</>}</button>
                        {empPending && <button style={mkBtn('green', sendingNow === empPending.id)} disabled={sendingNow === empPending.id} onClick={() => sendNow(empPending.id, emp.name)}>{sendingNow === empPending.id ? <Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : <><Zap size={13} strokeWidth={2} /> Send Now</>}</button>}
                        <button style={mkBtn('ghost')} onClick={() => toggleActive(emp)}>Deactivate</button>
                        <button style={mkBtn('danger', deleting)} disabled={deleting} onClick={() => deleteEmployee(emp)}>{deleting ? <Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : <><Trash2 size={13} strokeWidth={2} /> Delete</>}</button>
                      </div>
                    </div>
                    {!empPending && (
                      <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px dashed rgba(139,69,19,0.15)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '700', color: useCustom ? BROWN : '#8C8275', cursor: 'pointer' }}>
                          <input type="checkbox" checked={useCustom} onChange={e => setScheduleMode(p => ({ ...p, [emp.id]: e.target.checked }))} style={{ accentColor: BROWN, width: '13px', height: '13px' }} />Override send time?
                        </label>
                        {useCustom ? <input type="datetime-local" value={customDate[emp.id] || ''} onChange={e => setCustomDate(p => ({ ...p, [emp.id]: e.target.value }))} style={{ ...inp, width: 'auto', fontSize: '12px', padding: '6px 10px' }} /> : <span style={{ fontSize: '11px', color: '#8C8275' }}>Default: <strong>{sendLabel}</strong> at 12:00 AM CT · Pay period ending: <strong>{weekEnd}</strong></span>}
                      </div>
                    )}
                  </div>
                );
              })}
              {inactiveEmps.length > 0 && (
                <details style={{ marginTop: '8px' }}>
                  <summary style={{ fontSize: '12px', fontWeight: '700', color: '#8C8275', cursor: 'pointer', padding: '6px 0' }}>{inactiveEmps.length} inactive employee{inactiveEmps.length !== 1 ? 's' : ''}</summary>
                  <div style={{ marginTop: '8px' }}>
                    {inactiveEmps.map(emp => (
                      <div key={emp.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'rgba(0,0,0,0.02)', borderRadius: '10px', marginBottom: '6px', opacity: 0.6 }}>
                        <span style={{ fontSize: '13px', color: '#8C8275' }}>{emp.name} — <span style={{ fontSize: '11px' }}>{emp.email}</span></span>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button style={mkBtn('ghost')} onClick={() => toggleActive(emp)}>Reactivate</button>
                          <button style={mkBtn('danger', deletingEmp === emp.id)} disabled={deletingEmp === emp.id} onClick={() => deleteEmployee(emp)}>{deletingEmp === emp.id ? <Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : <><Trash2 size={13} strokeWidth={2} /> Delete</>}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
          {tab === 'history' && (
            <div>
              {stubs.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#8C8275', fontSize: '13px' }}>No history yet.</div>}
              {failed.length > 0 && <div style={{ marginBottom: '20px' }}><div style={{ fontSize: '11px', fontWeight: '700', color: '#A13D2D', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Failed</div>{failed.map(s => <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(161,61,45,0.04)', border: '1px solid rgba(161,61,45,0.15)', borderRadius: '10px', marginBottom: '6px' }}><div style={{ flex: 1 }}><div style={{ fontSize: '13px', fontWeight: '700', color: '#A13D2D' }}>{s.employeeName}</div><div style={{ fontSize: '11px', color: '#8C8275', marginTop: '2px' }}>Pay period ending {s.weekEnding}</div></div><button style={mkBtn('danger', sendingNow === s.id)} disabled={sendingNow === s.id} onClick={() => sendNow(s.id, s.employeeName)}>{sendingNow === s.id ? <Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : 'Retry'}</button></div>)}</div>}
              {pending.length > 0 && <div style={{ marginBottom: '20px' }}><div style={{ fontSize: '11px', fontWeight: '700', color: '#A6752A', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Pending</div>{pending.map(s => { const payDate = s.scheduledFor?.toDate ? s.scheduledFor.toDate() : null, lbl = payDate ? payDateLabel(payDate) : '—', we = payDate ? fmtDateShort(calcWeekEnding(payDate)) : s.weekEnding; return <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(166,117,42,0.06)', border: '1px solid rgba(166,117,42,0.3)', borderRadius: '10px', marginBottom: '6px' }}><div style={{ flex: 1 }}><div style={{ fontSize: '13px', fontWeight: '700', color: '#2B2620' }}>{s.employeeName}</div><div style={{ fontSize: '11px', color: '#8C8275', marginTop: '2px' }}>Pay period ending {we}</div></div><div style={{ fontSize: '11px', color: '#A6752A', textAlign: 'right' }}><div style={{ fontWeight: '700' }}>Sends: {lbl}</div><div>at 12:00 AM CT</div></div><button style={mkBtn('green', sendingNow === s.id)} disabled={sendingNow === s.id} onClick={() => sendNow(s.id, s.employeeName)}>{sendingNow === s.id ? <Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : <><Zap size={13} strokeWidth={2} /> Send Now</>}</button></div>; })}</div>}
              {sent.length > 0 && <div><div style={{ fontSize: '11px', fontWeight: '700', color: '#3B6B44', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Sent</div>{sent.map(s => <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: 'rgba(59,107,68,0.04)', border: '1px solid rgba(59,107,68,0.12)', borderRadius: '10px', marginBottom: '6px' }}><div style={{ flex: 1 }}><div style={{ fontSize: '13px', fontWeight: '700', color: '#2B2620' }}>{s.employeeName}</div><div style={{ fontSize: '11px', color: '#8C8275', marginTop: '2px' }}>Pay period ending {s.weekEnding}</div></div><div style={{ textAlign: 'right' }}><div style={{ fontSize: '11px', color: '#3B6B44', fontWeight: '600' }}>{formatTs(s.sentAt)}</div><div style={{ fontSize: '10px', color: '#8C8275', marginTop: '2px' }}>{s.sentBy === 'manual' ? 'Sent manually' : 'Auto-sent'} · PDF deleted</div></div></div>)}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// DIRECT EMAIL SECTION
// ═══════════════════════════════════════════════════════════════════════════════
function DirectEmailSection({ onBack }) {
  const { userProfile, currentUser } = useAuth();
  const senderName = userProfile?.name || currentUser?.email?.split('@')[0] || 'Office';
  const functions = getFunctions(), sendEmailFn = httpsCallable(functions, 'sendDirectEmail');
  const [to, setTo] = useState(''), [toName, setToName] = useState(''), [subject, setSubject] = useState(''), [body, setBody] = useState('');
  const [sending, setSending] = useState(false), [justSent, setJustSent] = useState(false), [attachment, setAttachment] = useState(null);
  const [searchQ, setSearchQ] = useState(''), [customers, setCustomers] = useState([]), [employees, setEmployees] = useState([]);
  const [showDrop, setShowDrop] = useState(false), [sentLog, setSentLog] = useState([]), [showLog, setShowLog] = useState(false);
  const fileRef = useRef(null);
  useEffect(() => { const u = onSnapshot(query(collection(db, 'payrollEmployees'), orderBy('name')), s => setEmployees(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(e => e.active && e.email))); return u; }, []);
  useEffect(() => { const u = onSnapshot(query(collection(db, 'sentEmails'), orderBy('sentAt', 'desc'), limit(50)), s => setSentLog(s.docs.map(d => ({ id: d.id, ...d.data() })))); return u; }, []);
  // One-time query, not a live listener — a recipient typeahead doesn't need
  // real-time sync, and a live onSnapshot here was re-subscribing (and billing
  // a fresh read per matched doc) on every single keystroke.
  useEffect(() => {
    if (!searchQ.trim() || searchQ.length < 2) { setCustomers([]); return; }
    const lower = searchQ.toLowerCase();
    let cancelled = false;
    getDocs(query(collection(db, 'customers'), limit(30))).then(s => {
      if (cancelled) return;
      setCustomers(s.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => ((c.name || c.customerName || '').toLowerCase().includes(lower) || (c.email || '').toLowerCase().includes(lower)) && c.email).slice(0, 6));
    });
    return () => { cancelled = true; };
  }, [searchQ]);
  function pickRecipient(email, name) { setTo(email); setToName(name || email); setSearchQ(''); setCustomers([]); setShowDrop(false); }
  async function handleAttachment(file) { const base64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result.split(',')[1]); r.onerror = rej; r.readAsDataURL(file); }); setAttachment({ base64, name: file.name }); }
  const effectiveTo = to.trim() || (searchQ.includes('@') ? searchQ.trim() : '');
  async function handleSend() {
    if (!effectiveTo || !subject.trim() || !body.trim()) { alert('Please fill in recipient, subject, and message.'); return; } setSending(true);
    try { await sendEmailFn({ to: effectiveTo, subject: subject.trim(), body: body.trim(), attachmentBase64: attachment?.base64 || null, attachmentName: attachment?.name || null, sentByName: senderName }); setJustSent(true); setTimeout(() => { setJustSent(false); setTo(''); setToName(''); setSubject(''); setBody(''); setAttachment(null); setSearchQ(''); }, 3000); }
    catch(e) { alert('Failed: ' + e.message); } finally { setSending(false); }
  }
  const suggestions = [...employees.map(e => ({ label: e.name, sub: e.email, email: e.email, type: 'employee' })), ...customers.map(c => ({ label: c.name || c.customerName, sub: c.email, email: c.email, type: 'customer' }))].filter((v, i, a) => a.findIndex(x => x.email === v.email) === i).filter(s => !searchQ || s.label?.toLowerCase().includes(searchQ.toLowerCase()) || s.email?.toLowerCase().includes(searchQ.toLowerCase()));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}><button style={mkBtn('ghost')} onClick={onBack}><ArrowLeft size={13} strokeWidth={2} /> Back</button><div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 600, color: '#2B2620' }}>Direct Email</div></div>
      <div style={{ ...card, minHeight: 'calc(100vh - 220px)', display: 'flex', flexDirection: 'column' }}>
        <div style={cardHeader('#3B6B44')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}><div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#3B6B44', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Mail size={18} strokeWidth={2} color="white" /></div><div><div style={{ fontSize: '15px', fontWeight: '800', color: '#2B2620' }}>Compose Email</div><div style={{ fontSize: '11px', color: '#8C8275' }}>From: The Window Place LLC · Replies go to nk@thewindowplacellc.com</div></div></div>
          <div style={{ display: 'flex', gap: '6px' }}>{sentLog.length > 0 && <span style={bdg('rgba(59,107,68,0.1)', '#3B6B44')}>{sentLog.length} sent</span>}<button style={mkBtn('ghost')} onClick={() => setShowLog(v => !v)}>{showLog ? <><Pencil size={13} strokeWidth={2} /> Compose</> : <><History size={13} strokeWidth={2} /> Sent Log</>}</button></div>
        </div>
        <div style={{ padding: '20px 24px', flex: 1, overflowY: 'auto' }}>
          {showLog ? (
            <div>{sentLog.length === 0 && <div style={{ textAlign: 'center', padding: '40px', color: '#8C8275', fontSize: '13px' }}>No emails sent yet.</div>}{sentLog.map(e => <div key={e.id} style={{ padding: '14px 16px', background: '#faf8f5', border: '1px solid rgba(59,107,68,0.1)', borderRadius: '12px', marginBottom: '10px' }}><div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '6px' }}><div><div style={{ fontSize: '13px', fontWeight: '700', color: '#2B2620' }}>{e.subject}</div><div style={{ fontSize: '11px', color: '#8C8275', marginTop: '2px' }}>To: {e.to} · {e.sentByName || 'Office'}{e.hasAttachment && <span style={{ marginLeft: '6px', display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Paperclip size={10} strokeWidth={2} /> {e.attachmentName}</span>}</div></div><div style={{ fontSize: '11px', color: '#8C8275', whiteSpace: 'nowrap' }}>{formatTs(e.sentAt)}</div></div><div style={{ fontSize: '12px', color: '#8C8275', lineHeight: '1.6', padding: '10px 12px', background: 'white', borderRadius: '8px', border: '1px solid rgba(59,107,68,0.08)', whiteSpace: 'pre-wrap', maxHeight: '80px', overflow: 'hidden' }}>{e.body}</div></div>)}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <FL label="To — Recipient Email">
                <div style={{ position: 'relative' }}>
                  {to ? (<div style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(59,107,68,0.05)', borderColor: 'rgba(59,107,68,0.3)' }}><span style={{ fontSize: '13px', color: '#3B6B44', fontWeight: '600' }}>{toName || to}</span><button onClick={() => { setTo(''); setToName(''); }} style={{ background: 'none', border: 'none', color: '#8C8275', cursor: 'pointer', display: 'flex', padding: '0 4px' }}><X size={14} strokeWidth={2} /></button></div>)
                  : (<input value={searchQ} onChange={e => { setSearchQ(e.target.value); setShowDrop(true); }} onFocus={() => setShowDrop(true)} onBlur={() => setTimeout(() => setShowDrop(false), 160)} name="twp-comm-email-to" autoComplete="off" placeholder="Search customers, employees, or type an email…" style={inp} />)}
                  {showDrop && !to && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid rgba(139,69,19,0.15)', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '220px', overflowY: 'auto', marginTop: '4px' }}>
                      {searchQ.includes('@') && <div onMouseDown={() => pickRecipient(searchQ, searchQ)} style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(139,69,19,0.06)' }} onMouseEnter={e => e.currentTarget.style.background = '#faf8f5'} onMouseLeave={e => e.currentTarget.style.background = 'white'}><Mail size={14} strokeWidth={2} color={STONE} /><div><div style={{ fontSize: '13px', fontWeight: '600' }}>Send to: {searchQ}</div><div style={{ fontSize: '11px', color: '#8C8275' }}>Free-form address</div></div></div>}
                      {suggestions.map((s, i) => <div key={i} onMouseDown={() => pickRecipient(s.email, s.label)} style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', borderBottom: '1px solid rgba(139,69,19,0.04)' }} onMouseEnter={e => e.currentTarget.style.background = '#faf8f5'} onMouseLeave={e => e.currentTarget.style.background = 'white'}>{s.type === 'employee' ? <User size={14} strokeWidth={2} color={STONE} /> : <Home size={14} strokeWidth={2} color={STONE} />}<div><div style={{ fontSize: '13px', fontWeight: '600', color: '#2B2620' }}>{s.label}</div><div style={{ fontSize: '11px', color: '#8C8275' }}>{s.sub}</div></div></div>)}
                      {suggestions.length === 0 && !searchQ.includes('@') && searchQ.length > 1 && <div style={{ padding: '14px', textAlign: 'center', fontSize: '12px', color: '#8C8275' }}>No matches — type a full email to send anyway</div>}
                    </div>
                  )}
                </div>
              </FL>
              <FL label="Subject"><input value={subject} onChange={e => setSubject(e.target.value)} name="twp-comm-email-subject" autoComplete="off" placeholder="e.g. Your Installation is Scheduled" style={inp} /></FL>
              <FL label="Message"><textarea value={body} onChange={e => setBody(e.target.value)} placeholder={"Type your message here…\n\nYour message will be wrapped in The Window Place LLC branded template automatically."} rows={6} style={{ ...inp, resize: 'vertical', lineHeight: '1.6' }} /></FL>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <input type="file" accept="application/pdf" style={{ display: 'none' }} ref={fileRef} onChange={e => { if (e.target.files[0]) handleAttachment(e.target.files[0]); e.target.value = ''; }} />
                {attachment ? (<div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 12px', background: 'rgba(59,107,68,0.06)', border: '1px solid rgba(59,107,68,0.2)', borderRadius: '8px' }}><Paperclip size={13} strokeWidth={2} color="#3B6B44" /><span style={{ fontSize: '12px', fontWeight: '600', color: '#3B6B44' }}>{attachment.name}</span><button onClick={() => setAttachment(null)} style={{ background: 'none', border: 'none', color: '#8C8275', cursor: 'pointer', display: 'flex' }}><X size={13} strokeWidth={2} /></button></div>) : <button style={mkBtn('ghost')} onClick={() => fileRef.current?.click()}><Paperclip size={13} strokeWidth={2} /> Attach PDF (optional)</button>}
                <div style={{ flex: 1 }} />
                <button style={justSent ? { ...mkBtn('green'), background: 'rgba(59,107,68,0.15)', color: '#3B6B44' } : mkBtn('teal', sending || !effectiveTo || !subject.trim() || !body.trim())} disabled={sending || !effectiveTo || !subject.trim() || !body.trim()} onClick={handleSend}>{justSent ? <><CheckCircle2 size={13} strokeWidth={2} /> Sent!</> : sending ? <><Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> Sending…</> : <><Mail size={13} strokeWidth={2} /> Send Email</>}</button>
              </div>
              <div style={{ fontSize: '11px', color: '#8C8275', fontStyle: 'italic' }}>From: The Window Place LLC · Reply-To: nk@thewindowplacellc.com · Branded template · Confidential notice included</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SMS SECTION
// ═══════════════════════════════════════════════════════════════════════════════
function SmsSection({ onBack }) {
  const confirm = useConfirm();
  const { userProfile, currentUser } = useAuth();
  const senderName   = userProfile?.name || currentUser?.email?.split('@')[0] || 'Office';
  const senderUid    = currentUser?.uid || null;

  const [conversations, setConversations]   = useState([]);
  const [activeConvo, setActiveConvo]       = useState(null);
  const [messages, setMessages]             = useState([]);
  const [newMsg, setNewMsg]                 = useState('');
  const [sending, setSending]               = useState(false);
  const [newPhone, setNewPhone]             = useState('');
  const [showNewConvo, setShowNewConvo]     = useState(false);
  const [searchQ, setSearchQ]               = useState('');
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showArchived, setShowArchived]     = useState(false);

  const [phoneNameMap, setPhoneNameMap] = useState({});
  const [renaming, setRenaming]         = useState(false);
  const [renameVal, setRenameVal]       = useState('');
  const [savingName, setSavingName]     = useState(false);
  const [ctxMenu, setCtxMenu]           = useState(null);
  const [hoveredMsg, setHoveredMsg]     = useState(null);
  const [copiedMsgId, setCopiedMsgId]   = useState(null);

  const messagesEndRef = useRef(null);
  const mediaFileRef   = useRef(null);

  // ── Phone→name maps ───────────────────────────────────────────────────────
  // One-time fetches, not live listeners — this is a lookup table for
  // resolving "New Contact"/"Unknown" conversation names to a real customer
  // name. It doesn't need to stay in sync live; a live onSnapshot here was
  // re-reading up to 500 customers + 1000 jobs on every unrelated change to
  // either collection for as long as the SMS Inbox stayed open.
  useEffect(() => {
    getDocs(query(collection(db, 'customers'), limit(500))).then(snap => {
      setPhoneNameMap(prev => {
        const next = { ...prev };
        snap.docs.forEach(d => {
          const data = d.data();
          const name   = (data.name || data.customerName || '').trim();
          const digits = normalizePhone(data.phone || data.phoneNormalized || '');
          if (name && digits.length === 10) next[digits] = name;
        });
        return next;
      });
    });
  }, []);

  useEffect(() => {
    getDocs(query(collection(db, 'jobs'), limit(1000))).then(snap => {
      setPhoneNameMap(prev => {
        const next = { ...prev };
        snap.docs.forEach(d => {
          const data   = d.data();
          const name   = (data.customerName || data.name || '').trim();
          const digits = normalizePhone(data.phoneNumber || '');
          if (name && digits.length === 10 && !next[digits]) next[digits] = name;
        });
        return next;
      });
    });
  }, []);

  // ── Resolve display name ──────────────────────────────────────────────────
  const resolveConvoName = useCallback((convo) => {
    const stored = (convo.customerName || '').trim();
    const digits = normalizePhone(convo.phoneE164);
    if (!isUnresolved(stored)) return stored;
    const looked = digits ? phoneNameMap[digits] : null;
    if (looked) {
      updateDoc(doc(db, 'conversations', convo.id), { customerName: looked }).catch(() => {});
      return looked;
    }
    return formatPhone(convo.phoneE164);
  }, [phoneNameMap]);

  async function saveRename() {
    if (!renameVal.trim() || !activeConvo) return;
    setSavingName(true);
    try { await updateDoc(doc(db, 'conversations', activeConvo), { customerName: renameVal.trim() }); setRenaming(false); setRenameVal(''); }
    catch(e) { alert('Failed to save: ' + e.message); } finally { setSavingName(false); }
  }

  async function deleteConversation(convoId, displayName) {
    if (!(await confirm({ title: `Delete conversation with ${displayName}?`, message: 'This removes all messages and cannot be undone.', tone: 'danger', confirmText: 'Delete' }))) return;
    try {
      const msgsSnap = await getDocs(collection(db, 'conversations', convoId, 'messages'));
      const batch = writeBatch(db);
      msgsSnap.docs.forEach(d => batch.delete(d.ref));
      batch.delete(doc(db, 'conversations', convoId));
      await batch.commit();
      if (activeConvo === convoId) setActiveConvo(null);
    } catch(e) { alert('Failed to delete: ' + e.message); }
  }

  async function deleteMessage(msgId) {
    if (!activeConvo) return;
    try { await deleteDoc(doc(db, 'conversations', activeConvo, 'messages', msgId)); }
    catch(e) { alert('Failed to delete message: ' + e.message); }
  }

  async function markUnread(convoId) {
    try { await updateDoc(doc(db, 'conversations', convoId), { unreadCount: 1 }); }
    catch(e) { alert('Failed: ' + e.message); }
  }

  async function toggleArchive(convoId, currentlyArchived) {
    try {
      await updateDoc(doc(db, 'conversations', convoId), { archived: !currentlyArchived });
      if (!currentlyArchived && activeConvo === convoId) setActiveConvo(null);
    } catch(e) { alert('Failed: ' + e.message); }
  }

  async function copyMessage(msgId, text) {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMsgId(msgId);
      setTimeout(() => setCopiedMsgId(null), 1500);
    } catch { /* clipboard not available */ }
  }

  // ── Conversations listener ────────────────────────────────────────────────
  // Bounded — an unbounded live listener here re-reads every conversation on
  // every message anywhere, for as long as the SMS Inbox is open. 500 is far
  // more than this business will realistically have open/recent at once;
  // archived + old conversations older than that just won't show in the list.
  useEffect(() => {
    const u = onSnapshot(query(collection(db, 'conversations'), orderBy('lastMessageAt', 'desc'), limit(500)),
      s => setConversations(s.docs.map(d => ({ id: d.id, ...d.data() }))));
    return u;
  }, []);

  // ── Messages listener ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeConvo) { setMessages([]); return; }
    const u = onSnapshot(
      query(collection(db, 'conversations', activeConvo, 'messages'), orderBy('createdAt', 'asc')),
      s => setMessages(s.docs.map(d => ({ id: d.id, ...d.data() })))
    );
    return u;
  }, [activeConvo]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // ── Mark read + write lastOpenedBy when opening a convo ──────────────────
  useEffect(() => {
    if (!activeConvo) return;
    updateDoc(doc(db, 'conversations', activeConvo), {
      unreadCount: 0,
      lastOpenedByUserId:   senderUid,
      lastOpenedByUserName: senderName,
      lastOpenedAt:         serverTimestamp(),
    }).catch(() => {});
    setRenaming(false); setRenameVal('');
  }, [activeConvo, senderUid, senderName]);

  useEffect(() => {
    if (!ctxMenu) return;
    const close = () => setCtxMenu(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [ctxMenu]);

  // ── Send message — writes assignedUserId + assignedUserName ──────────────
  async function handleSend(mediaUrl = null, mediaType = null) {
    const convo = conversations.find(c => c.id === activeConvo);
    if (!convo || sending) return;
    if (!newMsg.trim() && !mediaUrl) return;
    setSending(true);
    const msgBody = newMsg.trim();
    setNewMsg('');
    try {
      const msgData = {
        direction: 'outbound',
        body: msgBody || (mediaType?.includes('pdf') ? '📎 PDF attached' : '📷 Image attached'),
        toPhone: convo.phoneE164,
        fromPhone: 'TWP_OFFICE',
        sentByName: senderName,
        sentByUserId: senderUid,
        createdAt: serverTimestamp(),
        deliveryStatus: 'pending',
      };
      if (mediaUrl) msgData.media = { url: mediaUrl, type: mediaType || 'image/jpeg' };
      await addDoc(collection(db, 'conversations', activeConvo, 'messages'), msgData);
      // ── KEY: write assignedUserId + assignedUserName so iOS and web
      //        both show who last handled this conversation ──────────────────
      await updateDoc(doc(db, 'conversations', activeConvo), {
        lastMessageText:    msgData.body,
        lastMessageAt:      serverTimestamp(),
        lastMessageFrom:    'staff',
        updatedAt:          serverTimestamp(),
        archived:           false,
        assignedUserId:     senderUid,
        assignedUserName:   senderName,
      });
    } catch(e) { alert('Failed: ' + e.message); setNewMsg(msgBody); }
    finally { setSending(false); }
  }

  async function handleMediaFile(file) {
    if (!file || !activeConvo) return;
    setUploadingMedia(true);
    try {
      const storage = getStorage();
      const ext = file.name.split('.').pop() || 'bin';
      const path = `smsMedia/${activeConvo}/${Date.now()}.${ext}`;
      const ref = storageRef(storage, path);
      await uploadBytes(ref, file);
      const url = await getDownloadURL(ref);
      await handleSend(url, file.type);
    } catch(e) { alert('Media upload failed: ' + e.message); }
    finally { setUploadingMedia(false); if (mediaFileRef.current) mediaFileRef.current.value = ''; }
  }

  async function startNewConvo() {
    const digits = newPhone.replace(/\D/g, '');
    if (digits.length !== 10) { alert('Enter a valid 10-digit US phone number.'); return; }
    const conversationId = `conversation_${digits}`;
    const convoRef = doc(db, 'conversations', conversationId);
    const convoSnap = await getDoc(convoRef);
    const resolvedName = phoneNameMap[digits] || 'New Contact';
    if (!convoSnap.exists()) {
      await setDoc(convoRef, { phoneE164: `+1${digits}`, phoneNormalized: digits, customerName: resolvedName, unreadCount: 0, archived: false, createdAt: serverTimestamp(), updatedAt: serverTimestamp(), lastMessageAt: serverTimestamp(), lastMessageFrom: 'staff', lastMessageText: '' });
    } else if (isUnresolved(convoSnap.data().customerName) && resolvedName !== 'New Contact') {
      await updateDoc(convoRef, { customerName: resolvedName });
    }
    setActiveConvo(conversationId); setShowNewConvo(false); setNewPhone('');
  }

  function formatPhone(e164) {
    const d = (e164 || '').replace(/\D/g, '').slice(-10);
    if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
    return e164 || '—';
  }
  function fmtTime(ts) {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const isToday = d.toDateString() === new Date().toDateString();
    return isToday
      ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  const filtered = conversations.filter(c => {
    if (!!c.archived !== showArchived) return false;
    if (!searchQ.trim()) return true;
    const name = resolveConvoName(c).toLowerCase();
    return name.includes(searchQ.toLowerCase()) || (c.phoneE164 || '').includes(searchQ.replace(/\D/g, ''));
  });

  const activeConvoData    = conversations.find(c => c.id === activeConvo);
  const activeDisplayName  = activeConvoData ? resolveConvoName(activeConvoData) : '—';
  const totalUnread        = conversations.filter(c => !c.archived).reduce((s, c) => s + (c.unreadCount || 0), 0);
  const archivedCount      = conversations.filter(c => c.archived).length;

  function buildMessageRows(msgs) {
    const rows = [];
    let lastDay = null;
    msgs.forEach(msg => {
      if (!msg.createdAt) { rows.push({ type: 'msg', msg }); return; }
      const d = msg.createdAt.toDate ? msg.createdAt.toDate() : new Date(msg.createdAt);
      const dayKey = d.toDateString();
      if (dayKey !== lastDay) { rows.push({ type: 'sep', label: dateSeparatorLabel(msg.createdAt) }); lastDay = dayKey; }
      rows.push({ type: 'msg', msg });
    });
    return rows;
  }

  return (
    <div>
      {/* Context menu */}
      {ctxMenu && (
        <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: ctxMenu.y, left: ctxMenu.x, background: PAPER, border: '1px solid rgba(139,69,19,0.15)', borderRadius: '10px', boxShadow: '0 8px 28px rgba(0,0,0,0.14)', zIndex: 9999, minWidth: '180px', overflow: 'hidden' }}>
          {(() => {
            const convo = conversations.find(c => c.id === ctxMenu.convoId);
            if (!convo) return null;
            const name = resolveConvoName(convo);
            return (
              <>
                <div style={{ padding: '10px 14px', borderBottom: '1px solid rgba(139,69,19,0.08)', fontSize: '11px', fontWeight: '700', color: STONE }}>{name}</div>
                {[
                  { Icon: Pencil, label: 'Rename contact', action: () => { setActiveConvo(ctxMenu.convoId); setTimeout(() => { setRenaming(true); setRenameVal(isUnresolved(convo.customerName) ? '' : (convo.customerName || '')); }, 50); setCtxMenu(null); } },
                  { Icon: Circle, label: 'Mark as unread', action: () => { markUnread(ctxMenu.convoId); setCtxMenu(null); } },
                  { Icon: convo.archived ? ArchiveRestore : Archive, label: convo.archived ? 'Unarchive' : 'Archive chat', action: () => { toggleArchive(ctxMenu.convoId, convo.archived); setCtxMenu(null); } },
                  { Icon: Trash2, label: 'Delete chat', danger: true, action: () => { setCtxMenu(null); deleteConversation(ctxMenu.convoId, name); } },
                ].map((item, i) => (
                  <div key={i} onClick={item.action} style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: '600', color: item.danger ? RUST : INK, borderBottom: i < 3 ? '1px solid rgba(139,69,19,0.05)' : 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = item.danger ? 'rgba(161,61,45,0.05)' : '#faf8f5'}
                    onMouseLeave={e => e.currentTarget.style.background = PAPER}>
                    <item.Icon size={14} strokeWidth={2} />{item.label}
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
        <button style={mkBtn('ghost')} onClick={onBack}><ArrowLeft size={13} strokeWidth={2} /> Back</button>
        <div>
          <div style={{ fontFamily: FONT_DISPLAY, fontSize: '20px', fontWeight: 600, color: '#2B2620' }}>SMS Inbox</div>
          {totalUnread > 0 && <div style={{ fontSize: '12px', color: '#A13D2D', fontWeight: '700', marginTop: '2px' }}>{totalUnread} unread message{totalUnread !== 1 ? 's' : ''}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#A6752A', fontWeight: '600', marginTop: '2px' }}><Smartphone size={11} strokeWidth={2} /> TWP Text Number: (605) 401-2814 — share this with customers</div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
          {archivedCount > 0 && <button style={mkBtn('ghost')} onClick={() => setShowArchived(v => !v)}>{showArchived ? <><ArchiveRestore size={13} strokeWidth={2} /> Active Chats</> : <><Archive size={13} strokeWidth={2} /> Archived ({archivedCount})</>}</button>}
          <button style={mkBtn('blue')} onClick={() => setShowNewConvo(v => !v)}>{showNewConvo ? <><X size={13} strokeWidth={2} /> Cancel</> : '+ New Message'}</button>
        </div>
      </div>

      {showNewConvo && (
        <div style={{ ...card, padding: '16px 20px', marginBottom: '16px', display: 'flex', gap: '10px', alignItems: 'end' }}>
          <FL label="Phone Number" style={{ flex: 1 }}>
            <input value={newPhone} onChange={e => setNewPhone(e.target.value)} name="twp-comm-sms-newphone" autoComplete="off" placeholder="e.g. 6054321234" style={inp} onKeyDown={e => e.key === 'Enter' && startNewConvo()} />
          </FL>
          <button style={mkBtn('blue')} onClick={startNewConvo}>Open Conversation</button>
        </div>
      )}

      <div style={{ ...card, height: 'calc(100vh - 220px)', minHeight: '500px', display: 'flex', flexDirection: 'column', marginBottom: 0 }}>
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* ── Conversation list ──────────────────────────────────────────── */}
          <div style={{ width: '280px', flexShrink: 0, borderRight: '1px solid rgba(139,69,19,0.08)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ padding: '10px 12px', borderBottom: '1px solid rgba(139,69,19,0.06)' }}>
              <input value={searchQ} onChange={e => setSearchQ(e.target.value)} name="twp-comm-sms-search" autoComplete="off" placeholder="Search by name or number…" style={{ ...inp, fontSize: '12px', padding: '6px 10px' }} />
            </div>
            {showArchived && <div style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 12px', background: 'rgba(139,69,19,0.04)', borderBottom: '1px solid rgba(139,69,19,0.06)', fontSize: '11px', fontWeight: '700', color: '#8C8275' }}><Archive size={11} strokeWidth={2} /> Archived chats</div>}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {filtered.length === 0 && <div style={{ padding: '24px', textAlign: 'center', color: '#8C8275', fontSize: '12px' }}>{showArchived ? 'No archived chats' : 'No conversations yet'}</div>}
              {filtered.map(convo => {
                const isActive    = convo.id === activeConvo;
                const hasUnread   = convo.unreadCount > 0;
                const displayName = resolveConvoName(convo);
                return (
                  <div key={convo.id}
                    onClick={() => setActiveConvo(convo.id)}
                    onContextMenu={e => { e.preventDefault(); setCtxMenu({ convoId: convo.id, x: e.clientX, y: e.clientY }); }}
                    style={{
                      padding: '11px 13px', cursor: 'pointer',
                      background: isActive ? 'rgba(166,117,42,0.07)' : 'transparent',
                      borderLeft: isActive ? '3px solid #A6752A' : '3px solid transparent',
                      borderBottom: '1px solid rgba(139,69,19,0.05)', transition: 'all 0.15s', position: 'relative',
                    }}
                    onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(139,69,19,0.03)'; e.currentTarget.querySelector('.ctx-btn').style.opacity = '1'; }}
                    onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; e.currentTarget.querySelector('.ctx-btn').style.opacity = '0'; }}>

                    {/* Row: name + time + ⋮ */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
                      <span style={{ fontSize: '13px', fontWeight: hasUnread ? '800' : '600', color: hasUnread ? '#2B2620' : '#2B2620', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        {hasUnread && <span style={{ ...bdg('rgba(166,117,42,0.12)', '#A6752A'), fontSize: '10px' }}>{convo.unreadCount}</span>}
                        <button className="ctx-btn" onClick={e => { e.stopPropagation(); setCtxMenu({ convoId: convo.id, x: e.clientX, y: e.clientY }); }} style={{ opacity: 0, background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: '4px', fontSize: '14px', color: '#8C8275', lineHeight: 1, transition: 'opacity 0.15s' }} title="Options">⋮</button>
                      </div>
                    </div>

                    {/* Phone */}
                    <div style={{ fontSize: '11px', color: '#8C8275', marginBottom: '3px' }}>{formatPhone(convo.phoneE164)}</div>

                    {/* Last message preview */}
                    <div style={{ fontSize: '11px', color: '#8C8275', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '5px' }}>{convo.lastMessageText || 'No messages yet'}</div>

                    {/* ── Assigned chip — matches iOS green/orange pill ── */}
                    <AssignedChip name={convo.assignedUserName} small />

                    {/* Last touched line */}
                    {convo.assignedUserName && convo.lastMessageAt && (
                      <div style={{ fontSize: '10px', color: '#8C8275', marginTop: '3px' }}>
                        Last touched by {convo.assignedUserName} · {relativeTime(convo.lastMessageAt)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Message pane ──────────────────────────────────────────────── */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {!activeConvo ? (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#8C8275', gap: '8px' }}>
                <MessageSquare size={34} strokeWidth={1.5} color="#8C8275" />
                <div style={{ fontSize: '14px', fontWeight: '600' }}>Select a conversation</div>
                <div style={{ fontSize: '12px' }}>or tap "+ New Message" above</div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(139,69,19,0.08)', display: 'flex', alignItems: 'center', gap: '10px', background: 'rgba(166,117,42,0.03)' }}>
                  {/* Avatar with colored ring (green = assigned, orange = unassigned) */}
                  <div style={{
                    width: '34px', height: '34px', borderRadius: '50%',
                    background: '#A6752A',
                    color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '13px', fontWeight: '800', flexShrink: 0,
                    outline: `2px solid ${activeConvoData?.assignedUserName ? 'rgba(59,107,68,0.6)' : 'rgba(166,117,42,0.5)'}`,
                    outlineOffset: '2px',
                  }}>
                    {activeDisplayName.charAt(0).toUpperCase()}
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    {renaming ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <input autoFocus value={renameVal} onChange={e => setRenameVal(e.target.value)} name="twp-comm-sms-rename" autoComplete="off"
                          onKeyDown={e => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') { setRenaming(false); setRenameVal(''); } }}
                          placeholder="Enter customer name…" style={{ ...inp, fontSize: '13px', padding: '5px 10px', width: '180px' }} />
                        <button style={mkBtn('blue', savingName || !renameVal.trim())} disabled={savingName || !renameVal.trim()} onClick={saveRename}>{savingName ? <Loader2 size={13} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : <><Check size={13} strokeWidth={2} /> Save</>}</button>
                        <button style={mkBtn('ghost')} onClick={() => { setRenaming(false); setRenameVal(''); }}><X size={13} strokeWidth={2} /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '700', color: '#2B2620' }}>{activeDisplayName}</div>
                          <div style={{ fontSize: '11px', color: '#8C8275' }}>{formatPhone(activeConvoData?.phoneE164)}</div>
                        </div>
                        <button title="Rename contact" onClick={() => { setRenaming(true); setRenameVal(isUnresolved(activeConvoData?.customerName) ? '' : (activeConvoData?.customerName || '')); }}
                          style={{ background: 'none', border: '1px solid rgba(139,69,19,0.2)', borderRadius: '6px', cursor: 'pointer', padding: '3px 7px', display: 'flex', color: '#8C8275' }}><Pencil size={12} strokeWidth={2} /></button>
                        {/* ── Assigned chip in header ── */}
                        <AssignedChip name={activeConvoData?.assignedUserName} />
                      </div>
                    )}

                    {/* Last touched / last opened lines — mirrors iOS header */}
                    {!renaming && (
                      <div style={{ display: 'flex', gap: '12px', marginTop: '3px', flexWrap: 'wrap' }}>
                        {activeConvoData?.assignedUserName && activeConvoData?.lastMessageAt && (
                          <span style={{ fontSize: '10px', color: '#8C8275' }}>
                            Last touched by <strong>{activeConvoData.assignedUserName}</strong> · {relativeTime(activeConvoData.lastMessageAt)}
                          </span>
                        )}
                        {activeConvoData?.lastOpenedByUserName && (
                          <span style={{ fontSize: '10px', color: '#8C8275' }}>
                            Opened by {activeConvoData.lastOpenedByUserName}
                            {activeConvoData.lastOpenedAt ? ` · ${relativeTime(activeConvoData.lastOpenedAt)}` : ''}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Header action buttons */}
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button title="Mark as unread" onClick={() => markUnread(activeConvo)} style={{ background: 'none', border: '1px solid rgba(139,69,19,0.15)', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px', display: 'flex', color: '#8C8275' }}><Circle size={13} strokeWidth={2} /></button>
                    <button title={activeConvoData?.archived ? 'Unarchive chat' : 'Archive chat'} onClick={() => toggleArchive(activeConvo, activeConvoData?.archived)}
                      style={{ background: 'none', border: '1px solid rgba(139,69,19,0.15)', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px', fontSize: '12px', color: '#8C8275' }}>
                      {activeConvoData?.archived ? <ArchiveRestore size={13} strokeWidth={2} /> : <Archive size={13} strokeWidth={2} />}
                    </button>
                    <button title="Delete conversation" onClick={() => deleteConversation(activeConvo, activeDisplayName)}
                      style={{ background: 'none', border: '1px solid rgba(161,61,45,0.2)', borderRadius: '6px', cursor: 'pointer', padding: '4px 8px', display: 'flex', color: '#A13D2D' }}><Trash2 size={13} strokeWidth={2} /></button>
                  </div>
                </div>

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {messages.length === 0 && <div style={{ textAlign: 'center', color: '#8C8275', fontSize: '12px', marginTop: '40px' }}>No messages yet</div>}
                  {buildMessageRows(messages).map((row, idx) => {
                    if (row.type === 'sep') return (
                      <div key={`sep-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '10px 0 6px' }}>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(139,69,19,0.1)' }} />
                        <span style={{ fontSize: '11px', fontWeight: '600', color: '#8C8275', whiteSpace: 'nowrap' }}>{row.label}</span>
                        <div style={{ flex: 1, height: '1px', background: 'rgba(139,69,19,0.1)' }} />
                      </div>
                    );

                    const msg     = row.msg;
                    const isOut   = msg.direction === 'outbound';
                    const failed  = msg.deliveryStatus === 'failed';
                    const media   = msg.media;
                    const mediaDisplayUrl = media?.url ? proxyUrl(media.url) : null;
                    const isPdf   = media?.type?.includes('pdf') || (media?.url || '').includes('.pdf');
                    const isVideo = media?.type?.includes('video') || (media?.url || '').includes('.mp4');
                    const isHovered = hoveredMsg === msg.id;
                    const isCopied  = copiedMsgId === msg.id;
                    const hasText   = msg.body && !['📎 PDF attached', '📷 Image attached'].includes(msg.body);

                    return (
                      <div key={msg.id}
                        onMouseEnter={() => setHoveredMsg(msg.id)}
                        onMouseLeave={() => setHoveredMsg(null)}
                        style={{ display: 'flex', flexDirection: 'column', alignItems: isOut ? 'flex-end' : 'flex-start', marginBottom: '4px', position: 'relative' }}>

                        {isHovered && (
                          <div style={{ position: 'absolute', [isOut ? 'left' : 'right']: 0, top: '50%', transform: 'translateY(-50%)', display: 'flex', gap: '4px', zIndex: 10 }}>
                            {hasText && (
                              <button onClick={() => copyMessage(msg.id, msg.body)} title="Copy text"
                                style={{ background: isCopied ? 'rgba(59,107,68,0.1)' : 'white', border: '1px solid rgba(139,69,19,0.15)', borderRadius: '6px', cursor: 'pointer', padding: '3px 6px', display: 'flex', color: isCopied ? '#3B6B44' : '#8C8275', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                                {isCopied ? <Check size={12} strokeWidth={2} /> : <Copy size={12} strokeWidth={2} />}
                              </button>
                            )}
                            <button onClick={async () => { if (await confirm({ title: 'Delete this message?', tone: 'danger', confirmText: 'Delete' })) deleteMessage(msg.id); }} title="Delete message"
                              style={{ background: 'white', border: '1px solid rgba(161,61,45,0.2)', borderRadius: '6px', cursor: 'pointer', padding: '3px 6px', display: 'flex', color: '#A13D2D', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                              <Trash2 size={12} strokeWidth={2} />
                            </button>
                          </div>
                        )}

                        {media && !msg.mediaExpired && mediaDisplayUrl && (
                          <div style={{ marginBottom: '3px', maxWidth: '240px' }}>
                            {isPdf ? (
                              <a href={mediaDisplayUrl} target="_blank" rel="noreferrer" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: isOut ? 'rgba(166,117,42,0.12)' : '#f0f0f0', borderRadius: '10px', textDecoration: 'none', color: isOut ? '#A6752A' : '#2B2620', fontSize: '13px', fontWeight: '600', border: '1px solid rgba(166,117,42,0.15)' }}><Paperclip size={13} strokeWidth={2} /> View PDF</a>
                            ) : isVideo ? (
                              <video src={mediaDisplayUrl} controls style={{ width: '100%', borderRadius: '10px', display: 'block' }} />
                            ) : (
                              <img src={mediaDisplayUrl} alt="attachment" style={{ width: '100%', maxWidth: '240px', borderRadius: '10px', display: 'block', cursor: 'pointer' }} onClick={() => window.open(mediaDisplayUrl, '_blank')}
                                onError={e => { const p = e.target.parentNode; if (p) { const a = document.createElement('a'); a.href = media.url; a.target = '_blank'; a.rel = 'noreferrer'; a.textContent = 'View Image'; a.style.cssText = 'display:block;padding:10px 14px;background:#f0f0f0;border-radius:10px;text-decoration:none;color:#444;font-size:12px;'; p.replaceChild(a, e.target); } }} />
                            )}
                          </div>
                        )}
                        {msg.mediaExpired && <div style={{ fontSize: '11px', color: '#8C8275', fontStyle: 'italic', marginBottom: '2px' }}>Attachment expired</div>}

                        {hasText && (
                          <div style={{ maxWidth: '70%', padding: '8px 13px', borderRadius: isOut ? '14px 14px 4px 14px' : '14px 14px 14px 4px', background: isOut ? (failed ? 'rgba(161,61,45,0.1)' : '#A6752A') : '#f0f0f0', color: isOut ? (failed ? '#A13D2D' : 'white') : '#2B2620', fontSize: '13px', lineHeight: '1.5', userSelect: 'text' }}>
                            {msg.body}
                          </div>
                        )}

                        {/* Timestamp + sender name under every outbound bubble */}
                        <div style={{ fontSize: '10px', color: '#8C8275', marginTop: '2px', display: 'flex', gap: '5px' }}>
                          <span>{fmtTime(msg.createdAt)}</span>
                          {isOut && <span>· {msg.sentByName || 'Office'}</span>}
                          {failed && <span style={{ color: '#A13D2D', fontWeight: '700' }}>· Failed to send</span>}
                          {msg.deliveryStatus === 'sending' && <span>· Sending…</span>}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Compose */}
                <div style={{ padding: '10px 14px', borderTop: '1px solid rgba(139,69,19,0.08)', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <input type="file" accept="image/*,application/pdf,video/*" style={{ display: 'none' }} ref={mediaFileRef} onChange={e => { if (e.target.files[0]) handleMediaFile(e.target.files[0]); }} />
                  <button title="Attach image or PDF" style={{ ...mkBtn('ghost', uploadingMedia), padding: '9px 11px', flexShrink: 0 }} disabled={uploadingMedia} onClick={() => mediaFileRef.current?.click()}>{uploadingMedia ? <Loader2 size={14} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : <Paperclip size={14} strokeWidth={2} />}</button>
                  <textarea value={newMsg} onChange={e => setNewMsg(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                    placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                    rows={2} style={{ ...inp, resize: 'none', flex: 1, fontSize: '13px', borderColor: 'rgba(166,117,42,0.2)', lineHeight: '1.5' }} />
                  <button style={mkBtn('blue', sending || (!newMsg.trim() && !uploadingMedia))} disabled={sending || (!newMsg.trim() && !uploadingMedia)} onClick={() => handleSend()}>{sending ? <Loader2 size={14} strokeWidth={2} style={{animation:'spin 0.8s linear infinite'}} /> : '→ Send'}</button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
export default function CommunicationsPage() {
  const { userProfile } = useAuth();
  const isOfficeManager = userProfile?.role === 'officeManager';
  const { unreadSmsActive: unreadSms } = useNotificationCounts();
  const [section, setSection]               = useState(null);
  const [pendingPayroll, setPendingPayroll] = useState(0);
  const [sentEmailCount, setSentEmailCount] = useState(0);

  useEffect(() => { if (!isOfficeManager && (section === 'payroll' || section === 'email')) setSection(null); }, [section, isOfficeManager]);
  useEffect(() => { const u = onSnapshot(query(collection(db, 'payStubs'), limit(50)), s => setPendingPayroll(s.docs.filter(d => d.data().status === 'pending').length)); return u; }, []);
  useEffect(() => { const u = onSnapshot(query(collection(db, 'sentEmails'), limit(50)), s => setSentEmailCount(s.size)); return u; }, []);

  const breadcrumb = `Dashboard › Communications${section === 'sms' ? ' › SMS' : section === 'email' ? ' › Direct Email' : section === 'payroll' ? ' › Payroll' : ''}`;

  return (
    <AppLayout title="Communications" breadcrumb={breadcrumb}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <div style={{ display: 'flex', flexDirection: 'column', height: (section === 'sms' || section === 'email') ? 'calc(100vh - 120px)' : 'auto', fontFamily: FONT_SANS }}>
        {!section    && <Landing onSelect={setSection} unreadSms={unreadSms} pendingPayroll={pendingPayroll} sentEmailCount={sentEmailCount} isOfficeManager={isOfficeManager} />}
        {section === 'payroll' && <PayrollSection onBack={() => setSection(null)} />}
        {section === 'email'   && <DirectEmailSection onBack={() => setSection(null)} />}
        {section === 'sms'     && <SmsSection onBack={() => setSection(null)} />}
      </div>
    </AppLayout>
  );
}